<template>
  <div>
    <!-- Shared Courses shell (ACC-355) -->
    <courses-panel
      v-if="courseCount"
      v-model="activeTab"
      :tabs="courseTabs"
      :variant="variant"
      @add-course="handleAddCoursesClick"
    >
      <template v-if="activeTab === 'past' && deadlineApproachingCodes.length" #banner>
        <transcript-deadline-banner :course-codes="deadlineApproachingCodes" />
      </template>

      <template #subheader="{ activeTab }">{{ subheaderText(activeTab) }}</template>

      <template #tab-current>
        <template v-if="currentCourses.length > 0">
          <courses-section-heading v-bind="sectionCopy('current')" />
          <my-courses
            v-for="(course, index) in currentCourses"
            :key="course._enrollmentId ?? index"
            v-bind="getCourseProps(course, 'My Current Courses')"
            @doUnenroll="doUnenroll($event)"
            @openCloseUnenrollModel="openCloseUnenrollModel"
            @reloadDashboardOrPathway="reloadDashboardOrPathway"
            @alreadyRequestedForCredit="alreadyRequestedForCredit($event)"
            @dropdown-toggle="handleDropdownToggle"
          />
        </template>
        <courses-empty-state v-else v-bind="emptyState('current')" @cta="handleAddCoursesClick" />
      </template>

      <template #tab-upcoming>
        <template v-if="upcomingCourses.length > 0">
          <courses-section-heading v-bind="sectionCopy('upcoming')" />
          <my-courses
            v-for="(course, index) in upcomingCourses"
            :key="course._enrollmentId ?? index"
            v-bind="getCourseProps(course, 'My Upcoming Courses')"
            @doUnenroll="doUnenroll($event)"
            @openCloseUnenrollModel="openCloseUnenrollModel"
            @reloadDashboardOrPathway="reloadDashboardOrPathway"
            @alreadyRequestedForCredit="alreadyRequestedForCredit($event)"
            @dropdown-toggle="handleDropdownToggle"
          />
        </template>
        <courses-empty-state v-else v-bind="emptyState('upcoming')" @cta="handleAddCoursesClick" />
      </template>

      <!-- Transcript + expired + unenrolled, each under its own heading -->
      <template #tab-past>
        <template v-if="pastGroups.length">
          <template v-for="group in pastGroups" :key="group.key">
            <courses-section-heading v-if="group.section" v-bind="sectionCopy(group.section)" />
            <my-courses
              v-for="(course, index) in group.courses"
              :key="`${group.key}-${course._enrollmentId ?? index}`"
              v-bind="getCourseProps(course, group.title)"
              @doUnenroll="doUnenroll($event)"
              @openCloseUnenrollModel="openCloseUnenrollModel"
              @reloadDashboardOrPathway="reloadDashboardOrPathway"
              @alreadyRequestedForCredit="alreadyRequestedForCredit($event)"
              @dropdown-toggle="handleDropdownToggle"
            />
          </template>
        </template>
        <courses-empty-state v-else v-bind="emptyState('past')" @cta="handleAddCoursesClick" />
      </template>
    </courses-panel>

    <UnenrollDialog
      :show="unenrollCourse"
      :courseInfo="Object.assign({}, courseUnenrollmentInfo.courseInfo)"
      @openCloseUnenrollModel="openCloseUnenrollModel"
      @doUnenroll="doUnenroll"
      :paymentMethod="courseUnenrollmentInfo.paymentMethod"
      :enrollmentModeVerifiedDate="courseUnenrollmentInfo.enrollmentModeVerifiedDate"
      :courseDropDeadline="courseUnenrollmentInfo.courseDropDeadline"
      :enrollmentId="courseUnenrollmentInfo.enrollmentId"
      :buttonAction="courseUnenrollmentInfo.buttonAction"
      :enrollmentMode="courseUnenrollmentInfo.enrollmentMode"
      :isCourseRefunded="courseUnenrollInfo.isCourseRefunded"
      :verifiedTransactionId="courseUnenrollInfo.verifiedTransactionId"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useCoursesStore } from '@/stores/courses';
import { useUserStore } from '@/stores/user';

import UnenrollDialog from '../UnenrollDialog.vue';
import CoursesPanel from './CoursesPanel.vue';
import CoursesEmptyState from './CoursesEmptyState.vue';
import CoursesSectionHeading from './CoursesSectionHeading.vue';
import TranscriptDeadlineBanner from './TranscriptDeadlineBanner.vue';
import { emptyStateFor } from './courseEmptyStates';
import { courseGroupsFor } from './courseGroups';
import { sectionCopyFor } from './courseSectionCopy';
import { subheaderTextFor } from './courseSubheader';
import { deadlineApproachingCourseCodes } from './courseTranscriptState';
import { SessionInitializer } from '../../mixins/sessionInitializer';
import { Helper } from '../../mixins/helpers';
import { DataLayerMixin } from '../../mixins/dataLayerMixin';
import { Logger } from '../../mixins/logger';
export default defineComponent({
  name: 'DashboardCoursesPanel',
  emits: ['course-unenrolled'],
  mixins: [SessionInitializer, Helper, DataLayerMixin, Logger],

  data() {
    return {
      unenrollCourse: false,
      courseUnenrollInfo: {},
      activeTab: 'current',
      openDropdownId: null // only one course card's Options dropdown open at a time
    };
  },

  components: {
    UnenrollDialog,
    CoursesPanel,
    CoursesEmptyState,
    CoursesSectionHeading,
    TranscriptDeadlineBanner
  },

  props: {
    course_sections: {
      type: Array,
      default: () => []
    },
    // Forwarded to CoursesPanel: 'card' = rounded main-dashboard style, else default.
    variant: {
      type: String,
      default: 'default'
    }
  },

  computed: {
    courseCount() {
      return this.course_sections.filter((e) => e.courses.length > 0).length > 0;
    },
    courseTabs() {
      return [
        { value: 'current', label: 'Current' },
        { value: 'upcoming', label: 'Upcoming' },
        // badge = courses awaiting a transcript action (0 hides the badge)
        { value: 'past', label: 'Past', badge: this.transcriptCount }
      ];
    },
    // Transcript / transcripted / past courses are dropped from the dashboard
    // course_sections prop, so read them from the shared store instead.
    transcriptSections() {
      const sections = useCoursesStore().globalCourseSections || [];
      const coursesFor = (title) => sections.find((s) => s.section_title === title)?.courses || [];
      return {
        toAdd: coursesFor('Add to Transcript'),
        transcripted: coursesFor('Transcripted Courses'),
        past: coursesFor('Past Courses')
      };
    },
    // Delegates to the shared mapping so the dashboard and My Courses can't drift.
    pastGroups() {
      return courseGroupsFor('past', {
        transcriptToAdd: this.transcriptSections.toAdd,
        transcripted: this.transcriptSections.transcripted,
        past: this.transcriptSections.past
      });
    },
    transcriptCount() {
      return this.transcriptSections.toAdd.length;
    },
    // Codes for the transcript-deadline banner (deadline within 60 days).
    deadlineApproachingCodes() {
      return deadlineApproachingCourseCodes(this.transcriptSections.toAdd);
    },
    courseUnenrollmentInfo() {
      return this.courseUnenrollInfo;
    },
    currentCourses() {
      const currentSection = this.course_sections.find((section) => section.section_title === 'My Current Courses');
      return currentSection?.courses || [];
    },
    upcomingCourses() {
      const upcomingSection = this.course_sections.find((section) => section.section_title === 'My Upcoming Courses');
      return upcomingSection?.courses || [];
    }
  },

  methods: {
    handleDropdownToggle(courseId, isOpen) {
      this.openDropdownId = isOpen ? courseId : null;
    },
    emptyState(tab) {
      return emptyStateFor(tab);
    },
    sectionCopy(tab) {
      return sectionCopyFor(tab);
    },
    subheaderText(tab) {
      // The dashboard has no "all" tab, so that branch never fires here.
      return subheaderTextFor(tab, {
        current: this.currentCourses,
        upcoming: this.upcomingCourses,
        transcriptToAdd: this.transcriptSections.toAdd,
        transcripted: this.transcriptSections.transcripted,
        past: this.transcriptSections.past
      });
    },
    getCourseProps(course, sectionTitle) {
      return {
        title: course._courseInfo._courseName,
        idVerificationDeadline: course._idVerificationDeadline,
        coursePrefix: course._courseInfo._coursePrefix,
        coursePrefixDisplayLabel: course._courseInfo._coursePrefixDisplayLabel || '',
        courseStartDate: course._courseInfo._courseStartDate,
        credits: course._courseInfo._asuCredits,
        courseEndDate: course._courseInfo._courseEndDate,
        buttonLabel: course._buttonLabel,
        buttonAction: course._buttonAction,
        term: course._courseInfo._term,
        courseOfferingId: course._courseOfferingId,
        courseCompletedDate: course._courseCompletedDate,
        enrollmentDate: course._enrollmentDate,
        courseType: course._courseInfo._courseType,
        courseUrl: course._courseInfo._openEdxCourseUrl,
        courseSection: sectionTitle,
        openDropdownId: this.openDropdownId,
        shouldShowViewCourse: !['Transcripted Courses', 'Past Courses'].includes(sectionTitle),
        courseViewUrl: course._courseInfo._openEdxViewCourseUrl,
        // Past cards now render "View course results" here too, and it navigates to this URL.
        progressPageURL: course._progressPageURL,
        benefitsEligible: course._benefitsEligible,
        partnerBenefitEligible: course._partnerBenefitEligible,
        enrollmentStatus: course._enrollmentStatus,
        deadlineCounter: course._courseInfo._deadlineCounter,
        courseBrief: course._courseInfo._courseBrief,
        grade: course._grade || 0,
        currentGrade: course._currentGrade || '0',
        eaCourseURL: course._courseInfo._eaCourseURL,
        enrollmentId: course._enrollmentId,
        finalGrade: course._finalGrade || '',
        from: 'dashboard',
        enrollmentMode: course._enrollmentMode,
        courseDescription: course._courseInfo._courseDescription,
        benefitType: course._benefitType,
        unenroll: course._unenroll,
        creditConversionPrice: course._courseInfo._creditConversionPrice,
        creditEligibilityPrice: course._courseInfo._creditEligibilityPrice,
        courseInfo: course._courseInfo,
        openEdxCourseId: course._courseInfo._openEdxCourseId,
        transcriptedDate: course._transcriptedDate,
        creditWindowOverride: course._creditWindowOverride,
        canvasCourseId: course._canvasCourseId,
        LMS: course._courseInfo._lms,
        canvasEnrollmentId: course._courseInfo._canvasEnrollmentId,
        canvasUserId: course._canvasUserId || null,
        courseSelected: course._enrollmentStatus && course._enrollmentStatus.toLowerCase() === 'active',
        courseLength: course._courseInfo._courseLength,
        coursePrerequisiteLabel: course._courseInfo._coursePrerequisiteLabel,
        courseEffortPerHour: course._courseInfo._courseEffortPerHour,
        coursePacing: course._courseInfo._coursePacing,
        courseDesignations: course._courseInfo._courseDesignations,
        paymentMethod: course._paymentMethod,
        enrollmentModeVerifiedDate: course._enrollmentModeVerifiedDate,
        courseDropDeadline: course._courseDropDeadline,
        courseRegistrationDate: course._courseInfo._courseRegistrationDate,
        orientationStartDate: course._courseInfo._orientationStartDate,
        isPathwayCourse: course._isPathwayCourse,
        isCourseRefunded: course._isCourseRefunded,
        verifiedTransactionId: course._verifiedTransactionId,
        invisibleVoucher: course._invisibleVoucher,
        completionLtiUrl: course._completionLtiUrl,
        multiEnrollmentType: course._multiEnrollmentType
      };
    },
    openCloseUnenrollModel(event) {
      if (event) this.courseUnenrollInfo = event.courseUnenrollInfo;
      this.unenrollCourse = !this.unenrollCourse;
    },
    reloadDashboardOrPathway(event) {
      if (event) {
        this.getEnrollments((enrollments) => {
          this.logInfo('get enrollments', enrollments);
        });
      }
    },
    async closeCreditRequestError() {
      await this.reloadDashboardOrPathway(true);
      this.showCreditRequestError = !this.showCreditRequestError;
    },

    async doUnenroll(event) {
      try {
        this.logInfo('dounenroll started ' + JSON.stringify(event));

        const coursesStore = useCoursesStore();
        const usersStore = useUserStore();

        let enrollmentToUnenroll = coursesStore.enrollments.find((e) => e._enrollmentId === event.enrollmentId);

        if (!enrollmentToUnenroll) {
          this.logInfo('dounenroll - enrollment not found for id ' + event.enrollmentId);

          throw new Error('Enrollment not found for id ' + event.enrollmentId);
        }

        await this.$conn.unenrollCourse({
          learnerId: usersStore.apis.learnerData._learnerId,
          enrollmentId: enrollmentToUnenroll._enrollmentId,
          courseOfferingId: enrollmentToUnenroll._courseOfferingId,
          enrollmentStatus: 'Inactive'
        });

        this.updateLocalCourseSections(event.enrollmentId);

        // spinner loader will be cleared here
        this.getEnrollments((enrollments) => this.logInfo('get enrollments', enrollments));

        if (event.callback && typeof event.callback === 'function') {
          event.callback();
        }
      } catch (error) {
        this.logError('dounenroll failed', error);
      }
    },

    updateLocalCourseSections(enrollmentId) {
      // Create a local copy of course_sections with the unenrolled course removed
      const updatedSections = this.course_sections.map((section) => {
        if (section.courses && section.courses.length > 0) {
          const filteredCourses = section.courses.filter((course) => {
            if (!course._enrollmentId) return true; // Keep courses without valid info (shouldn't happen)

            // Remove the specific unenrolled course
            return course._enrollmentId !== enrollmentId;
          });

          return {
            ...section,
            courses: filteredCourses
          };
        }
        return section;
      });

      // Emit event to parent component (DashboardAccelerate) to update course_sections
      this.$emit('course-unenrolled', updatedSections);
    },
    handleAddCoursesClick() {
      this.asuoDataLayerClick('MyCourses', '0', {
        region: 'main content',
        section: 'current courses',
        text: 'add courses'
      });
      this.$router.push('/courses-add');
    },
    handleViewPastCourses() {
      this.asuoDataLayerClick('MyCourses', '0', {
        region: 'main content',
        section: 'my current courses',
        text: 'View your past courses'
      });
      this.$router.push('/courses-history');
    }
  }
});
</script>
